import type { ServiceItem } from "../types";

export const SERVICE_ICONS = [
  "stethoscope",
  "cpu",
  "sparkles",
  "shield",
  "hardDrive",
  "wifi",
] as const;

export type ServiceIcon = (typeof SERVICE_ICONS)[number];

export interface HeroContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  ctaPrimary: string;
  ctaSecondary: string;
}

export interface ServicesContent {
  heading: string;
  subtitle: string;
  ctaTitle: string;
  ctaText: string;
  items: ServiceItem[];
}

export interface HowItWorksContent {
  heading: string;
  subtitle: string;
  items: Array<{ n: string; title: string; text: string }>;
}

export interface ContactContent {
  heading: string;
  subtitle: string;
  name: string;
  phone: string;
  email: string;
  zone: string;
  coverage: string;
  whatsapp: string;
  whatsappMessage: string;
}

export interface SiteContent {
  hero: HeroContent;
  services: ServicesContent;
  howItWorks: HowItWorksContent;
  contact: ContactContent;
}

export const defaultContent: SiteContent = {
  hero: {
    eyebrow: "// reparação de computadores",
    title: "Reparação e manutenção de computadores",
    subtitle:
      "Reparação e manutenção de computadores em Setúbal e arredores. Diagnóstico, hardware, vírus, dados e redes — com preço indicado antes de avançar.",
    ctaPrimary: "Pedir Reparação",
    ctaSecondary: "Ver Serviços",
  },
  services: {
    heading: "Serviços",
    subtitle: "Portáteis, desktops e redes de casa ou escritório.",
    ctaTitle: "Não sabes qual é o problema?",
    ctaText: "Envia um pedido de diagnóstico — sem compromisso.",
    items: [
      {
        id: "diagnostico",
        title: "Diagnóstico",
        description: "Identificação da avaria com indicação de preço antes de qualquer reparação.",
        duration: "24h",
        icon: "stethoscope",
      },
      {
        id: "hardware",
        title: "Reparação de Hardware",
        description: "Substituição de componentes, limpeza interna e upgrades de desempenho.",
        duration: "24–48h",
        icon: "cpu",
      },
      {
        id: "manutencao",
        title: "Manutenção e Otimização",
        description: "Limpeza de sistema, atualizações e arranque mais rápido.",
        duration: "24h",
        icon: "sparkles",
      },
      {
        id: "malware",
        title: "Remoção de Vírus e Malware",
        description: "Limpeza de infeções, restauro do browser e reforço das defesas.",
        duration: "24–48h",
        icon: "shield",
      },
      {
        id: "dados",
        title: "Recuperação de Dados",
        description: "Tentativa de recuperação de discos, pens USB e partições.",
        duration: "48–72h",
        icon: "hardDrive",
      },
      {
        id: "redes",
        title: "Configuração de Redes",
        description: "Routers, Wi-Fi e redes de casa ou escritório.",
        duration: "mesmo dia",
        icon: "wifi",
      },
    ],
  },
  howItWorks: {
    heading: "Como funciona",
    subtitle: "Três passos, sem surpresas.",
    items: [
      { n: "01", title: "Pedido", text: "Descreve o problema pelo formulário no site." },
      { n: "02", title: "Diagnóstico", text: "Indicamos a causa, o preço e o prazo antes de avançar." },
      { n: "03", title: "Reparação", text: "Reparação no local ou recolha do equipamento, conforme o caso." },
    ],
  },
  contact: {
    heading: "Contacto",
    subtitle: "Pedido de reparação. O agendamento é confirmado depois do contacto.",
    name: "Bennworks Tech",
    phone: "+351 900 000 000",
    email: "tech@bennworks.pt",
    zone: "Setúbal e arredores",
    coverage: "Área de cobertura: Setúbal, Azeitão, Palmela e arredores.\nDeslocações combinadas no contacto.",
    whatsapp: "351900000000",
    whatsappMessage: "Olá, preciso de ajuda com um computador.",
  },
};

export const navItems = [
  { href: "#inicio", label: "Início" },
  { href: "#servicos", label: "Serviços" },
  { href: "#como-funciona", label: "Como funciona" },
  { href: "#contacto", label: "Contacto" },
] as const;

export function phoneHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, "");
  return `tel:${digits}`;
}

export function emailHref(email: string): string {
  return `mailto:${email}`;
}

export function isServiceIcon(value: string): value is ServiceIcon {
  return (SERVICE_ICONS as readonly string[]).includes(value);
}
